import type { ServerSortColumn } from "components/Table/Table";
import Table from "components/Table/Table";
import { commonColumnProps, OFFSET, PAGE_SIZE } from "constants/TableConstants";
import { useEffect, useState } from "react";
import type { CellClickArgs } from "react-data-grid";
import { useNavigate } from "react-router-dom";
import { setIsLoading } from "reducers/main";
import { useGetIntakeFilesQuery } from "services/file-api";
import type {
  ServerSortableFilesTableDataType,
  FileType,
} from "types/DataTypes";
import { useQuery } from "utils/hooks";
import { useDispatch } from "utils/redux";
import { GLOBAL_SEARCH_PARAM, useUrlStore } from "utils/useUrlStore";

import FilePageContainer from "./FilePageContainer";

/**
 * convert Table component sort columns to backend order_by format for files
 * send frontend column names directly - backend handles the conversion
 */
const convertSortToOrderBy = (
  sortColumns: readonly { columnKey: string; direction: "ASC" | "DESC" }[],
): string | null => {
  if (sortColumns.length === 0) {
    return null;
  }

  const { columnKey, direction } = sortColumns[0];
  // Send frontend column name directly - backend converts using its COLUMN_MAPPING
  return direction === "DESC" ? `-${columnKey}` : columnKey;
};

const FILE_COLUMNS = [
  { key: "id", name: "ID", ...commonColumnProps },
  { key: "is_rush", name: "Rush", ...commonColumnProps, width: 100 },
  { key: "due_date", name: "Due Date", ...commonColumnProps },
  { key: "date_referred", name: "Date Referred", ...commonColumnProps },
  { key: "source", name: "Source", ...commonColumnProps },
  { key: "datetime_closed", name: "Date Closed", ...commonColumnProps },
  { key: "client__name", name: "Client", ...commonColumnProps },
  { key: "employer__name", name: "Employer", ...commonColumnProps },
  { key: "status", name: "Status", ...commonColumnProps },
  { key: "services__type", name: "Services", ...commonColumnProps },
  { key: "claimants__first_name", name: "Claimant", ...commonColumnProps },
  { key: "claimants__birth_date", name: "Date of Birth", ...commonColumnProps },
  { key: "dates_of_injury", name: "Date of Injury", ...commonColumnProps },
];

const FILTER_NAME_MAP = {
  service_type: "service",
  file_status: "status",
};

const IntakeTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();
  const { getFilters } = useUrlStore();
  const filters = getFilters();
  const [page, setPage] = useState(1);
  const [sortColumns, setSortColumns] = useState<readonly ServerSortColumn[]>(
    [],
  );

  const transformedFilters = Object.entries(filters).reduce(
    (acc, [key, value]) => {
      const backendKey = FILTER_NAME_MAP[key] || key;
      acc[backendKey] = value;
      return acc;
    },
    {},
  );

  // convert sort columns to backend format
  const sortBy = convertSortToOrderBy(sortColumns);

  const {
    data: files = { results: [], count: 0 },
    isLoading,
    isFetching,
  } = useGetIntakeFilesQuery({
    searchTerm: query.get(GLOBAL_SEARCH_PARAM),
    filters: {
      ...transformedFilters,
      limit: PAGE_SIZE,
      offset: OFFSET(page),
    },
    sort_by: sortBy || "-id", // default to descending ID sort
  });

  const handleCellClick = (
    args: CellClickArgs<ServerSortableFilesTableDataType, unknown>,
  ) => {
    navigate(`/operations/files/${args.row.id}/edit`);
  };

  const handleSortChange = (newSortColumns: readonly ServerSortColumn[]) => {
    setSortColumns(newSortColumns);
    // reset to first page when sorting changes
    setPage(1);
  };

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  // filters live in the URL, so changing one leaves `page` where it was --
  // applying a filter from page 2+ would otherwise request an offset past the
  // end of the narrowed result set and show an empty table.
  const filterKey = JSON.stringify(filters);
  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  // helper to get priority icon based on rush status
  const getPriorityIcon = (file: FileType) => {
    if (file.is_24_hour_rush) {
      return (
        <span className="priority-cell" title="24 Hour Rush">
          <span className="material-icons priority-icon priority-urgent">
            circle
          </span>
          <span className="priority-label">24h</span>
        </span>
      );
    }
    if (file.is_3_day_rush) {
      return (
        <span className="priority-cell" title="3 Day Rush">
          <span className="material-icons priority-icon priority-high">
            circle
          </span>
          <span className="priority-label">3d</span>
        </span>
      );
    }
    if (file.is_rush) {
      return (
        <span className="priority-cell" title="Rush">
          <span className="material-icons priority-icon priority-medium">
            circle
          </span>
          <span className="priority-label">Rush</span>
        </span>
      );
    }
    return (
      <span className="priority-cell priority-none" title="No Rush">
        —
      </span>
    );
  };

  const rows =
    files.results?.map((file: FileType) => ({
      id: file.id,
      is_rush: getPriorityIcon(file), // display composite rush icon, but sort by is_rush field
      due_date: file.due_date,
      // files keyed in by operations have no date_referred; fall back to the
      // date the file was created
      date_referred: file.date_referred || file.created,
      source: file.source_verbose,
      datetime_closed: file.datetime_closed,
      status: file.status_verbose,
      date_of_birth: file.claimants[0]?.birth_date,
      dates_of_injury: file.dates_of_injury?.[0]?.date,
      // original keys for backward compatibility
      client_name: file.client?.name,
      employer_name: file.employer?.name,
      services: [...new Set(file.services.map((service) => service.type))].join(
        ", ",
      ),
      claimant_name:
        file.claimants?.length > 0
          ? `${file.claimants[0]?.first_name} ${file.claimants[0]?.last_name}`
          : "",
      // backend field names for server-side sorting
      client__name: file.client?.name,
      employer__name: file.employer?.name,
      services__type: [
        ...new Set(file.services.map((service) => service.type)),
      ].join(", "),
      claimants__first_name:
        file.claimants?.length > 0
          ? `${file.claimants[0]?.first_name} ${file.claimants[0]?.last_name}`
          : "",
      claimants__birth_date: file.claimants[0]?.birth_date, // for sorting
    })) || [];

  return (
    <FilePageContainer>
      <Table
        columns={FILE_COLUMNS}
        rows={rows}
        rowKeyGetter={(row: ServerSortableFilesTableDataType) => row.id}
        onCellClick={handleCellClick}
        isLoading={isFetching}
        fullHeight
        pagination={{
          pageSize: PAGE_SIZE,
          pageIndex: page,
          rowCount: files.count,
          onPageChange: (pageIndex) => setPage(pageIndex),
          isLoading: isFetching,
        }}
        useServerSorting={true}
        serverSorting={{
          sortColumns,
          onSortChange: handleSortChange,
        }}
      />
    </FilePageContainer>
  );
};

export default IntakeTable;
